import { useState, useEffect } from 'react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Slider } from '@/components/ui/slider'
import { Checkbox } from '@/components/ui/checkbox'
import { Card } from '@/components/ui/card'
import { Gauge, MapPin, Clock } from 'lucide-react'
import type { AlertCondition } from '@/types/fleetvision'

interface ConditionStepProps {
  state: any
  updateState: (updates: any) => void
}

export function ConditionStep({ state, updateState }: ConditionStepProps) {
  const [conditionType, setConditionType] = useState<'metric' | 'geofence' | 'hos'>('metric')
  const [geofences, setGeofences] = useState<any[]>([])

  // Metric threshold state
  const [metric, setMetric] = useState('speed_mph')
  const [operator, setOperator] = useState('>')
  const [value, setValue] = useState(70)
  const [aggregation, setAggregation] = useState('max')
  const [windowMinutes, setWindowMinutes] = useState(5)

  // Geofence event state
  const [selectedGeofences, setSelectedGeofences] = useState<string[]>([])
  const [direction, setDirection] = useState<'enter' | 'exit' | 'either'>('enter')

  // HOS threshold state
  const [hosMetric, setHosMetric] = useState('driving_hours')
  const [hosOperator, setHosOperator] = useState('<')
  const [hosValue, setHosValue] = useState(2)

  useEffect(() => {
    fetchGeofences()

    // Initialize from existing condition if editing
    if (state.condition) {
      const cond = state.condition
      if (cond.kind === 'metric_threshold') {
        setConditionType('metric')
        setMetric(cond.metric)
        setOperator(cond.operator)
        setValue(cond.value)
        setAggregation(cond.aggregation)
        setWindowMinutes(Math.floor(cond.windowSec / 60))
      } else if (cond.kind === 'geofence_event') {
        setConditionType('geofence')
        setSelectedGeofences(cond.geofenceIds || [])
        setDirection(cond.direction)
      } else if (cond.kind === 'hos_threshold') {
        setConditionType('hos')
        setHosMetric(cond.hosMetric)
        setHosOperator(cond.operator)
        setHosValue(cond.value)
      }
    }
  }, [])

  const fetchGeofences = async () => {
    try {
      const response = await fetch('/api/geofences')
      const data = await response.json()
      setGeofences(data || [])
    } catch (error) {
      console.error('Failed to fetch geofences:', error)
    }
  }

  const buildCondition = (): AlertCondition => {
    if (conditionType === 'metric') {
      return {
        kind: 'metric_threshold',
        metric,
        operator: operator as any,
        value,
        aggregation: aggregation as any,
        windowSec: windowMinutes * 60,
      }
    } else if (conditionType === 'geofence') {
      return {
        kind: 'geofence_event',
        geofenceIds: selectedGeofences.length > 0 ? selectedGeofences : undefined,
        direction,
      }
    } else if (conditionType === 'hos') {
      return {
        kind: 'hos_threshold',
        hosMetric: hosMetric as any,
        operator: hosOperator as any,
        value: hosValue,
      }
    }
    return null as any
  }

  useEffect(() => {
    // Update condition whenever any parameter changes
    const condition = buildCondition()
    updateState({ condition })
  }, [
    conditionType,
    metric,
    operator,
    value,
    aggregation,
    windowMinutes,
    selectedGeofences,
    direction,
    hosMetric,
    hosOperator,
    hosValue,
  ])

  const handleGeofenceToggle = (geofenceId: string, checked: boolean) => {
    setSelectedGeofences(
      checked
        ? [...selectedGeofences, geofenceId]
        : selectedGeofences.filter((id) => id !== geofenceId)
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-4">Signal & Condition</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Define when this alert should trigger
        </p>
      </div>

      <Tabs value={conditionType} onValueChange={(v) => setConditionType(v as any)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="metric">
            <Gauge className="h-4 w-4 mr-2" />
            Metric Threshold
          </TabsTrigger>
          <TabsTrigger value="geofence">
            <MapPin className="h-4 w-4 mr-2" />
            Geofence Event
          </TabsTrigger>
          <TabsTrigger value="hos">
            <Clock className="h-4 w-4 mr-2" />
            HOS Hours
          </TabsTrigger>
        </TabsList>

        {/* Metric Threshold */}
        <TabsContent value="metric" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Metric</Label>
              <Select value={metric} onValueChange={setMetric}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="speed_mph">Speed (mph)</SelectItem>
                  <SelectItem value="engine_temp">Engine temperature</SelectItem>
                  <SelectItem value="fuel_level">Fuel level (%)</SelectItem>
                  <SelectItem value="idle_time">Idle time (min)</SelectItem>
                  <SelectItem value="rpm">Engine RPM</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Operator</Label>
              <Select value={operator} onValueChange={setOperator}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=">">Greater than (&gt;)</SelectItem>
                  <SelectItem value="<">Less than (&lt;)</SelectItem>
                  <SelectItem value=">=">Greater or equal (≥)</SelectItem>
                  <SelectItem value="<=">Less or equal (≤)</SelectItem>
                  <SelectItem value="==">Equals (=)</SelectItem>
                  <SelectItem value="!=">Not equals (≠)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Threshold Value</Label>
            <Input
              type="number"
              value={value}
              onChange={(e) => setValue(Number(e.target.value))}
            />
          </div>

          <div className="space-y-2">
            <Label>Aggregation</Label>
            <Select value={aggregation} onValueChange={setAggregation}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="max">Max over window</SelectItem>
                <SelectItem value="avg">Average</SelectItem>
                <SelectItem value="min">Min</SelectItem>
                <SelectItem value="p95">95th percentile</SelectItem>
                <SelectItem value="stddevs_above_baseline">
                  Dynamic (σ above baseline)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Time Window: {windowMinutes} minute(s)</Label>
            <Slider
              min={1}
              max={60}
              step={1}
              value={[windowMinutes]}
              onValueChange={([v]) => setWindowMinutes(v)}
            />
            <p className="text-xs text-muted-foreground">
              Evaluate over the last {windowMinutes} minute(s)
            </p>
          </div>

          <Card className="p-4 bg-muted/50">
            <p className="text-sm">
              <strong>Example:</strong> Alert when {metric} {operator} {value} ({aggregation}) over
              the last {windowMinutes} minute(s)
            </p>
          </Card>
        </TabsContent>

        {/* Geofence Event */}
        <TabsContent value="geofence" className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Direction</Label>
            <Select value={direction} onValueChange={(v) => setDirection(v as any)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="enter">Enter geofence</SelectItem>
                <SelectItem value="exit">Exit geofence</SelectItem>
                <SelectItem value="either">Enter or exit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Geofences (leave empty for all)</Label>
            {geofences.length === 0 ? (
              <Card className="p-6 text-center text-muted-foreground">
                No geofences configured. Create geofences first.
              </Card>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                {geofences.map((geofence) => (
                  <div
                    key={geofence.id}
                    className="flex items-center space-x-2 p-2 hover:bg-accent rounded"
                  >
                    <Checkbox
                      id={geofence.id}
                      checked={selectedGeofences.includes(geofence.id)}
                      onCheckedChange={(checked) =>
                        handleGeofenceToggle(geofence.id, checked as boolean)
                      }
                    />
                    <Label htmlFor={geofence.id} className="flex-1 cursor-pointer">
                      {geofence.name}
                    </Label>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Card className="p-4 bg-muted/50">
            <p className="text-sm">
              <strong>Example:</strong> Alert when a vehicle {direction === 'either' ? 'enters or exits' : `${direction}s`}{' '}
              {selectedGeofences.length === 0
                ? 'any geofence'
                : selectedGeofences.length === 1
                ? 'the selected geofence'
                : `one of ${selectedGeofences.length} selected geofences`}
            </p>
          </Card>
        </TabsContent>

        {/* HOS Threshold */}
        <TabsContent value="hos" className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>HOS Metric</Label>
            <Select value={hosMetric} onValueChange={setHosMetric}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="driving_hours">Driving hours remaining</SelectItem>
                <SelectItem value="on_duty_hours">On-duty hours remaining</SelectItem>
                <SelectItem value="cycle_hours">Cycle hours remaining</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Operator</Label>
            <Select value={hosOperator} onValueChange={setHosOperator}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="<">Less than (&lt;)</SelectItem>
                <SelectItem value="<=">Less or equal (≤)</SelectItem>
                <SelectItem value=">">Greater than (&gt;)</SelectItem>
                <SelectItem value=">=">Greater or equal (≥)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Hours</Label>
            <Input
              type="number"
              step="0.5"
              value={hosValue}
              onChange={(e) => setHosValue(Number(e.target.value))}
            />
          </div>

          <Card className="p-4 bg-muted/50">
            <p className="text-sm">
              <strong>Example:</strong> Alert when {hosMetric.replace(/_/g, ' ')} {hosOperator} {hosValue} hour(s)
            </p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
